/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Campaign Intelligence overview", description = "Hightouch's Campaign Intelligence enables marketers to get self-serve access to all of their warehouse data.", lastUpdatedDate = new Date(1753401600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Campaign Intelligence overview",
        description: "Hightouch's Campaign Intelligence enables marketers to get self-serve access to all of their warehouse data.",
        lastUpdatedDate: "Fri Jul 25 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What's different about Campaign Intelligence?",
          "depth": 0,
          "id": "whats-different-about-campaign-intelligence"
        }, {
          "value": "The biggest benefits of using Campaign Intelligence are:",
          "depth": 1,
          "id": "the-biggest-benefits-of-using-campaign-intelligence-are"
        }, {
          "value": "Campaign Intelligence chart types",
          "depth": 0,
          "id": "campaign-intelligence-chart-types"
        }, {
          "value": "Insights",
          "depth": 1,
          "id": "insights"
        }, {
          "value": "Funnels",
          "depth": 1,
          "id": "funnels"
        }, {
          "value": "Splits",
          "depth": 1,
          "id": "splits"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Campaign Intelligence is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "No matter how great your marketing ideas are, your success hinges on your ability to try new things, understand what's working, and iterate quickly. Unfortunately, understanding what's working is a massive challenge today for most marketing teams. They struggle to analyze performance across channels and data silos, are often stuck with basic metrics, and have to rely on technical teams for deeper insights."
      }), "\n", _jsx(_components.p, {
        children: "We built Campaign Intelligence to tackle these challenges head on and give marketers analytic superpowers by combining access to complete cross-channel data in the data warehouse with unparalleled ease of use. Marketers can now independently build audiences, orchestrate cross-channel experiments, and measure campaign ROI–all within Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "whats-different-about-campaign-intelligence",
        children: "What's different about Campaign Intelligence?"
      }), "\n", _jsx(_components.p, {
        children: "Every marketing and advertising platform has its own built-in analytics capabilities–so why did we build Campaign Intelligence? These features are user-friendly, but they all suffer from incomplete data. Each platform only reports on data from its own campaigns and has different definitions for metrics like sessions and unique users. Marketers have to hop between them all to understand holistic performance and can't compare metrics apples-to-apples."
      }), "\n", _jsx(_components.p, {
        children: "For years, BI has been the answer. While these tools are flexible and can access all your data, the reality is they aren't designed for marketing teams. Inevitably, this results in a backlog of marketing requests to the data team."
      }), "\n", _jsx(_components.p, {
        children: "Campaign Intelligence bridges this gap. It lets marketers measure their activities in one central and trusted place, with apples-to-apples metrics across the entire customer journey. It's more user-friendly than BI and purpose-built for common marketing use cases."
      }), "\n", _jsx(_components.h3, {
        id: "the-biggest-benefits-of-using-campaign-intelligence-are",
        children: "The biggest benefits of using Campaign Intelligence are:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Explore complete data."
          }), " Make better decisions by analyzing all of your customer touchpoints and attributes across channels in the data warehouse. If you're missing some data, Hightouch can directly ingest campaigns from all major advertising and lifecycle marketing tools, powered by Fivetran."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Move faster."
          }), " Campaign Intelligence was built for marketers and is actually easy-to-use. Marketers can explore customer data and funnels on the fly without relying on data teams."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Close the loop between insights and action."
          }), " Deeply understand your customers while building audiences and journeys. Once a campaign goes live, easily understand what's working to help you build the next one."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "campaign-intelligence-chart-types",
        children: "Campaign Intelligence chart types"
      }), "\n", _jsx(_components.h3, {
        id: "insights",
        children: "Insights"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Line, Column, and Area charts:"
        }), " Analyze how trends change over time by aggregating events and filtering users. For example, see how many purchases your campaigns are driving."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Bar charts:"
        }), " Explore metrics by any dimension to understand what's driving your metrics. For example, breakdown LTV by acquisition channel to learn which touch point lead to the most valuable users."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Tables:"
        }), " Build a rich multi-metric, multi-dimensional analysis in a single view. Quickly analyze any combination of user behavior, user attributes, and metrics. For example, build a unified campaign table showing email sends, opens, and clicks along with any conversion data."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.a, {
          href: "/campaign-intelligence/insights",
          children: "Learn more about Insights"
        })
      }), "\n", _jsx(_components.table, {
        children: _jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "campaign-intel/insights/big_chart_line_chart_2.png",
                alt: "Line chart showing how many marketing emails were sent to two different audiences over the last 60 days."
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "campaign-intel/insights/big_chart_column_chart.png",
                alt: "Column chart comparing the number of users who have started a checkout process from two different audiences over the last 7 days."
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "campaign-intel/insights/big_chart_area_chart.png",
                alt: "Area chart showing the number of marketing emails sent to an audience over the last 30 days."
              })
            })]
          })
        })
      }), "\n", _jsx(_components.table, {
        children: _jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "campaign-intel/insights/big_chart_bar_chart.png",
                alt: "Bar chart comparing the number of users that live in each state."
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "campaign-intel/insights/big_chart_table_view.png",
                alt: "Table view comparing the number of users, per state, who have received marketing emails over the last 60 days."
              })
            })]
          })
        })
      }), "\n", _jsx(_components.h3, {
        id: "funnels",
        children: "Funnels"
      }), "\n", _jsx(_components.p, {
        children: "Diagnose where your users are dropping off. For example, if users are clicking on your emails but are not exploring past the landing page then you might need to adjust the content."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.a, {
          href: "/campaign-intelligence/funnels",
          children: "Learn more about Funnels"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/funnels.png",
          alt: "Funnels"
        })
      }), "\n", _jsx(_components.h3, {
        id: "splits",
        children: "Splits"
      }), "\n", _jsx(_components.p, {
        children: "Understand and measure the impact of your marketing efforts across randomized groups to determine which variant performed the best."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.a, {
          href: "/campaign-intelligence/splits",
          children: "Learn more about Splits"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/splits/splits-chart-normalized-per-member.png",
          alt: "Splits"
        })
      })]
    });
  }
}
export default MDXContent;
